/**
 * IntegrationsTab — Admin → Integrations: the honest map of every
 * connection Argo OS can make.
 *
 * This component is a thin, honest renderer over the pure registry in
 * src/lib/integrations.ts. It is the ONLY place that reads the real,
 * client-visible config — each seam's `isConfigured()` plus the Firestore
 * flag — and hands that snapshot to `buildIntegrations`. Status pills,
 * purpose, "why this status", and connect hints all come straight from the
 * registry, so what an operator sees is exactly what is (or isn't) wired.
 *
 * Two things it does beyond rendering:
 *   1. A "Setup" card at the top with a "Re-run guided setup" button that
 *      opens the exported OnboardingWizard as an overlay on demand (reuse,
 *      not fork — closing returns here; activation behaves exactly as
 *      first-run).
 *   2. NO fake "Connect" buttons. Where a connection is env/backend-side,
 *      the card SAYS so plainly (`connectHint`).
 */

import { useState } from 'react';
import { Compass, Plug, RefreshCw } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { OnboardingWizard } from '../views/onboarding/FirstRunWizard';
import { C } from '../lib/tokens';
import {
  buildIntegrations,
  type Integration,
  type IntegrationEnv,
  type IntegrationStatus,
} from '../lib/integrations';
import * as argoWorker from '../lib/agent/argoWorker';
import * as contentEngine from '../lib/agent/contentEngine';
import * as enrich from '../lib/agent/enrich';
import * as meetNotes from '../lib/agent/meetNotes';
import * as slackNotify from '../lib/agent/slackNotify';

/**
 * Read the REAL client-visible config snapshot. Every field is a live
 * `isConfigured()` (which each seam derives from its own VITE_* flag) plus
 * the Firestore gate that email queueing depends on — no aspiration.
 */
function readEnv(): IntegrationEnv {
  const firestore =
    Boolean(import.meta.env.VITE_FIREBASE_API_KEY) &&
    import.meta.env.VITE_USE_FIRESTORE === 'true';
  // The GitHub PR client (src/lib/integrations/github.ts) never reads the
  // token from env — it lives server-side. The browser is only TOLD whether
  // the backend repo is wired, via a non-secret VITE_GITHUB_REPO marker
  // ("owner/repo"), so HyperCoder can honestly show the loop connected.
  const githubRepo = Boolean(import.meta.env.VITE_GITHUB_REPO);
  return {
    agentWorker: argoWorker.isConfigured(),
    contentEngine: contentEngine.isConfigured(),
    enrich: enrich.isConfigured(),
    google: meetNotes.isConfigured(),
    slack: slackNotify.isConfigured(),
    firestore,
    githubRepo,
  };
}

const PILL: Record<IntegrationStatus, { label: string; fg: string; bg: string; border: string }> = {
  connected: {
    label: 'Connected',
    fg: C.green,
    bg: 'rgba(34,197,94,0.14)',
    border: 'rgba(34,197,94,0.45)',
  },
  partially: {
    label: 'Partial',
    fg: C.orange,
    bg: 'rgba(253,173,1,0.14)',
    border: 'rgba(253,173,1,0.45)',
  },
  not_connected: {
    label: 'Not connected',
    fg: C.textMuted,
    bg: 'rgba(255,255,255,0.05)',
    border: 'rgba(255,255,255,0.14)',
  },
};

function StatusPill({ status }: { status: IntegrationStatus }) {
  const p = PILL[status];
  return (
    <span
      data-testid={`pill-${status}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 9px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        color: p.fg,
        background: p.bg,
        border: `1px solid ${p.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        aria-hidden
        style={{ width: 7, height: 7, borderRadius: 999, background: p.fg, flexShrink: 0 }}
      />
      {p.label}
    </span>
  );
}

function IntegrationCard({ integration }: { integration: Integration }) {
  const { name, purpose, status, detail, connectHint, envManaged } = integration;
  return (
    <GlassPanel animate={false} style={{ padding: '16px 18px' }}>
      <div style={{ display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <h3 className="t-section-title" style={{ margin: 0, flex: 1 }}>
            {name}
          </h3>
          <StatusPill status={status} />
        </div>

        <p className="t-body" style={{ margin: 0, color: C.text }}>
          {purpose}
        </p>

        <p className="t-meta" style={{ margin: 0 }}>
          {detail}
        </p>

        <div
          style={{
            marginTop: 2,
            padding: '8px 10px',
            borderRadius: 8,
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
          }}
        >
          <Plug size={13} style={{ color: C.textDim, flexShrink: 0, marginTop: 2 }} />
          <span className="t-meta" style={{ color: C.textMuted }}>
            <strong style={{ color: C.text, fontWeight: 600 }}>
              {envManaged ? 'How to connect: ' : 'Status: '}
            </strong>
            {connectHint}
          </span>
        </div>
      </div>
    </GlassPanel>
  );
}

export function IntegrationsTab() {
  const [wizardOpen, setWizardOpen] = useState(false);
  // Read config once per mount — flags are deploy-time, so no need to poll.
  const integrations = buildIntegrations(readEnv());

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* ── Setup card: re-run the guided first-run wizard on demand ──── */}
      <GlassPanel variant="subtle" animate={false} style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <span
            style={{
              width: 38,
              height: 38,
              borderRadius: 12,
              display: 'inline-grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.12))',
              border: '1px solid rgba(255,0,255,0.28)',
              color: C.magentaSoft,
              flexShrink: 0,
            }}
          >
            <Compass size={19} strokeWidth={1.75} />
          </span>
          <div style={{ flex: 1, minWidth: 240 }}>
            <h2 className="t-section-title" style={{ margin: 0 }}>
              Forge setup
            </h2>
            <p className="t-meta" style={{ margin: '3px 0 0' }}>
              Re-run the guided first-run wizard any time — company voice, modules, personas,
              team and goals. It writes exactly the same way the first-run does.
            </p>
          </div>
          <button
            type="button"
            className="glass-btn glass-btn--primary"
            onClick={() => setWizardOpen(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <RefreshCw size={14} />
            Re-run guided setup
          </button>
        </div>
      </GlassPanel>

      {/* ── Intro ──────────────────────────────────────────────────────── */}
      <div>
        <h2 className="t-section-title" style={{ margin: 0 }}>
          Integrations
        </h2>
        <p className="t-meta" style={{ margin: '3px 0 0' }}>
          The honest map of every connection: what each one unlocks, whether it's actually
          wired right now, and exactly how to connect it. Statuses reflect real configuration
          only — never aspiration.
        </p>
      </div>

      {/* ── Grid of integration cards ──────────────────────────────────── */}
      <div
        style={{
          display: 'grid',
          gap: 14,
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        }}
      >
        {integrations.map((i) => (
          <IntegrationCard key={i.id} integration={i} />
        ))}
      </div>

      {wizardOpen && <OnboardingWizard onClose={() => setWizardOpen(false)} />}
    </div>
  );
}
